import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import css from './TimetableGrid.module.css';

const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday' };
const ALL_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DEFAULT_BREAKS = [{ afterPeriod: 3, label: 'Break', durationMinutes: 20 }];

async function request(fetchFn, path, options) {
  const result = await fetchFn(path, options);
  if (result && result.data !== undefined) return result;
  const data = await result.json().catch(() => ({}));
  return { ok: result.ok, status: result.status, data };
}

function entryFor(entries, day, slot, classId) {
  return (entries || []).find(entry => entry.dayOfWeek === day && Number(entry.slotOrder) === Number(slot) && (!classId || String(entry.classId) === String(classId)));
}

// Mirrors renderDayGroup's row/cell layout (including shared-cell rowSpans) so PDF/Excel exports match the on-screen grid exactly.
function buildExportSnapshot(timetable, daysToRender, mode, classes, entries) {
  const classLabelFor = classId => (timetable.classLabels || []).find(item => String(item.classId) === String(classId))?.label;
  return daysToRender.map(day => {
    const dayLabel = timetable.dayLabels?.[day.dayOfWeek] || DAY_LABELS[day.dayOfWeek] || day.dayOfWeek;
    const dayClasses = mode === 'multi' ? classes : [{ _id: entries[0]?.classId || 'student', name: entries[0]?.className || 'My Class' }];
    const slots = day.slots.map(slot => ({ order: slot.order, kind: slot.kind, label: slot.label, time: slot.startTime ? `${slot.startTime}–${slot.endTime}` : '' }));
    const rows = dayClasses.map((schoolClass, index) => ({
      className: classLabelFor(schoolClass._id) || schoolClass.name,
      cells: day.slots.map(slot => {
        if (slot.kind !== 'period') return index === 0 ? { shared: true, rowSpan: dayClasses.length, label: slot.label } : null;
        const entry = entryFor(timetable.entries, day.dayOfWeek, slot.order, mode === 'multi' ? schoolClass._id : null);
        return { shared: false, subjectName: entry?.subjectName || '', teacherName: entry?.teacherName || '', isConflict: !!entry?.isConflict, locked: !!entry?.locked, needsTeacher: !!entry?.needsTeacher };
      }),
    }));
    return { dayOfWeek: day.dayOfWeek, dayLabel, slots, rows };
  });
}

function Cell({ entry, slot, editable, onEdit, highlighted }) {
  if (slot.kind !== 'period') return null; // shared blocks are rendered as one merged cell by the caller
  return (
    <td className={`${css.cell} ${entry?.isConflict ? css.conflict : ''} ${entry?.locked ? css.locked : ''} ${highlighted ? css.editedCell : ''}`} onClick={() => editable && onEdit({ type: 'entry', entry, slot })}>
      <strong>{entry?.subjectName || (editable ? 'Assign subject' : '')}</strong>
      {entry?.teacherName && <span>{entry.teacherName}</span>}
      {entry?.locked && <small aria-label="Locked">Pinned</small>}
      {entry?.needsTeacher && <small className={css.needsTeacher} aria-label="No teacher assigned">No teacher</small>}
    </td>
  );
}

function EditModal({ editing, subjects, eligibleTeachers, onClose, onSave, onDelete, onChange, saving, error }) {
  const [deleteArmed, setDeleteArmed] = useState(false);
  const isEntry = editing.type === 'entry';
  const isSlot = editing.type === 'slot';
  const isDay = editing.type === 'day';
  const isClass = editing.type === 'class';
  const canDeletePeriod = isSlot && (editing.kind === 'period' || editing.kind === 'break');
  const deleteLabel = editing.kind === 'break' ? 'break' : 'period';
  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={event => event.stopPropagation()}>
        <h3 className={css.modalTitle}>Edit {isEntry ? 'timetable cell' : isSlot ? (editing.kind === 'break' ? 'break' : 'period header') : isDay ? 'day header' : 'class header'}</h3>
        <p className={css.modalHint}>Changes are saved to this timetable draft and will be visible to everyone after publishing.</p>
        {error && <div className={css.modalError}>{error}</div>}
        {isEntry && <div className={css.editForm}>
          <label className={css.fieldLabel}>Subject<select className={css.select} value={editing.subjectId} onChange={event => onChange({ ...editing, subjectId: event.target.value, teacherId: '' })}><option value="">Clear cell</option>{subjects.map(subject => <option key={subject._id} value={subject._id}>{subject.name}</option>)}</select></label>
          <label className={css.fieldLabel}>Teacher<select className={css.select} value={editing.teacherId || ''} onChange={event => onChange({ ...editing, teacherId: event.target.value })} disabled={!editing.subjectId}><option value="">Unassigned</option>{eligibleTeachers.map(teacher => <option key={teacher._id} value={teacher._id}>{teacher.firstName} {teacher.lastName}</option>)}</select></label>
        </div>}
        {isSlot && <div className={css.editForm}>
          <label className={css.fieldLabel}>{editing.kind === 'break' ? 'Break text' : 'Header text'}<input className={css.textInput} value={editing.label} onChange={event => onChange({ ...editing, label: event.target.value })} /></label>
          <label className={css.fieldLabel}>Start time<input className={css.textInput} type="time" value={editing.startTime || ''} onChange={event => onChange({ ...editing, startTime: event.target.value })} /></label>
          <label className={css.fieldLabel}>End time<input className={css.textInput} type="time" value={editing.endTime || ''} onChange={event => onChange({ ...editing, endTime: event.target.value })} /></label>
          {editing.kind !== 'period' && <label className={css.fieldLabel}>Block type<select className={css.select} value={editing.kind} onChange={event => onChange({ ...editing, kind: event.target.value })}><option value="break">Break</option><option value="assembly">Assembly</option><option value="activity">Activity</option></select></label>}
        </div>}
        {isDay && <label className={css.fieldLabel}>Day label<input className={css.textInput} value={editing.label} onChange={event => onChange({ ...editing, label: event.target.value })} /></label>}
        {isClass && <label className={css.fieldLabel}>Class label<input className={css.textInput} value={editing.label} onChange={event => onChange({ ...editing, label: event.target.value })} /></label>}
        {deleteArmed && <div className={css.deleteConfirm} role="alert"><strong>Delete {editing.label}?</strong><span>{editing.kind === 'break' ? `This removes the break from ${editing.dayOfWeek} and closes the timing gap.` : `This removes the period from ${editing.dayOfWeek} and shifts every later lesson left. The following break, if any, moves with it.`}</span><div className={css.deleteConfirmActions}><button type="button" className={css.secondaryBtn} onClick={() => setDeleteArmed(false)}>Keep {deleteLabel}</button><button type="button" className={css.dangerBtn} onClick={onDelete} disabled={saving}><DeleteIcon fontSize="small" /> Delete {deleteLabel}</button></div></div>}
        <div className={css.modalFooter}>
          <button type="button" className={css.secondaryBtn} onClick={onClose}>Cancel</button>
          {canDeletePeriod && !deleteArmed && <button type="button" className={css.dangerBtn} onClick={() => setDeleteArmed(true)} disabled={saving}><DeleteIcon fontSize="small" /> Delete {deleteLabel}</button>}
          {!deleteArmed && <button type="button" className={css.primaryBtn} onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>}
        </div>
      </div>
    </div>
  );
}

function TimetableSkeleton() {
  return (
    <div className={css.skeletonTableWrap} aria-label="Loading timetable" role="status">
      <div className={css.skeletonToolbar}>
        <span className={css.skel} />
        <span className={css.skel} />
      </div>
      <div className={css.skeletonTable}>
        {Array.from({ length: 5 }, (_, row) => (
          <div className={css.skeletonRow} key={row}>
            {Array.from({ length: 6 }, (_, column) => (
              <span className={css.skel} key={column} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Generate / Regenerate wizard ──────────────────────────────────────────────
function GenerateWizard({ classes, subjects, hasExistingTimetable, onClose, onSubmit, submitting, error }) {
  const [step, setStep] = useState(0);
  const [daysActive, setDaysActive] = useState(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
  const [periodsPerDay, setPeriodsPerDay] = useState(8);
  const [periodDurationMinutes, setPeriodDurationMinutes] = useState(40);
  const [schoolStartTime, setSchoolStartTime] = useState('08:00');
  const [breaks, setBreaks] = useState(DEFAULT_BREAKS);
  const [classIds, setClassIds] = useState(classes.map(cls => cls._id));
  const [pinnedSubjects, setPinnedSubjects] = useState([]);
  const [subjectFrequency, setSubjectFrequency] = useState([]);
  const [aiEnhance, setAiEnhance] = useState(false);

  const steps = ['Schedule', 'Breaks', 'Classes', 'Pinned Subjects', 'Frequency & AI', 'Review'];

  const toggleDay = day => setDaysActive(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));
  const toggleClass = classId => setClassIds(prev => (prev.includes(classId) ? prev.filter(id => id !== classId) : [...prev, classId]));

  const addBreak = () => setBreaks(prev => [...prev, { afterPeriod: 1, label: 'Break', durationMinutes: 15 }]);
  const updateBreak = (index, field, value) => setBreaks(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  const removeBreak = index => setBreaks(prev => prev.filter((_, i) => i !== index));

  const addPin = () => setPinnedSubjects(prev => [...prev, { subjectId: subjects[0]?._id || '', classId: '', dayOfWeek: '', periodNumber: 1 }]);
  const updatePin = (index, field, value) => setPinnedSubjects(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  const removePin = index => setPinnedSubjects(prev => prev.filter((_, i) => i !== index));

  const addFrequency = () => setSubjectFrequency(prev => [...prev, { subjectId: subjects[0]?._id || '', periodsPerWeek: 4 }]);
  const updateFrequency = (index, field, value) => setSubjectFrequency(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  const removeFrequency = index => setSubjectFrequency(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = () => {
    const scopedClassIds = classIds.length ? classIds : classes.map(cls => cls._id);
    // Global per-subject frequency overrides are expanded into concrete
    // per-class entries here, scoped to classes that actually offer the
    // subject and to the chosen class scope — the backend only understands
    // (classId, subjectId, periodsPerWeek) triples.
    const expandedFrequency = [];
    subjectFrequency.forEach(item => {
      if (!item.subjectId || !item.periodsPerWeek) return;
      const subject = subjects.find(entry => String(entry._id) === String(item.subjectId));
      if (!subject) return;
      (subject.classIds || []).filter(classId => scopedClassIds.some(id => String(id) === String(classId))).forEach(classId => {
        expandedFrequency.push({ classId, subjectId: item.subjectId, periodsPerWeek: Number(item.periodsPerWeek) });
      });
    });
    onSubmit({
      daysActive,
      periodsPerDay: Number(periodsPerDay),
      periodDurationMinutes: Number(periodDurationMinutes),
      schoolStartTime,
      breaks: breaks.map(item => ({ afterPeriod: Number(item.afterPeriod), label: item.label, durationMinutes: Number(item.durationMinutes) })),
      classIds: classIds.length ? classIds : undefined,
      pinnedSubjects: pinnedSubjects.filter(item => item.subjectId).map(item => ({ subjectId: item.subjectId, classId: item.classId || undefined, dayOfWeek: item.dayOfWeek || undefined, periodNumber: item.periodNumber ? Number(item.periodNumber) : undefined })),
      subjectFrequency: expandedFrequency,
      aiEnhance,
    });
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={event => event.stopPropagation()}>
        <h3 className={css.modalTitle}>{hasExistingTimetable ? 'Regenerate Timetable' : 'Generate Timetable'}</h3>
        <div className={css.wizardSteps}>{steps.map((label, index) => <span key={label} className={index === step ? css.wizardStepActive : ''}><b className={css.wizardStepNumber}>{index + 1}</b><span className={css.wizardStepLabel}>{label}</span></span>)}</div>
        {error && <div className={css.modalError}>{error}</div>}

        {step === 0 && (
          <div className={css.field}>
            <label className={css.fieldLabel}>Active days</label>
            <div className={css.checkRow}>{ALL_DAYS.map(day => <label key={day} className={css.checkOption}><input className="schoolCheckbox" type="checkbox" checked={daysActive.includes(day)} onChange={() => toggleDay(day)} />{DAY_LABELS[day]}</label>)}</div>
            <label className={css.fieldLabel}>Periods per day</label>
            <input className={css.textInput} type="number" min={1} max={12} value={periodsPerDay} onChange={event => setPeriodsPerDay(event.target.value)} />
            <label className={css.fieldLabel}>Period duration (minutes)</label>
            <input className={css.textInput} type="number" min={20} max={180} value={periodDurationMinutes} onChange={event => setPeriodDurationMinutes(event.target.value)} />
            <label className={css.fieldLabel}>School start time</label>
            <input className={css.textInput} type="time" value={schoolStartTime} onChange={event => setSchoolStartTime(event.target.value)} />
          </div>
        )}

        {step === 1 && (
          <div className={css.field}>
            <label className={css.fieldLabel}>Breaks</label>
            {breaks.map((item, index) => (
              <div key={index} className={css.ruleRow}>
                <span className={css.ruleLabel}>After period</span>
                <input className={css.smallInput} type="number" min={1} max={12} value={item.afterPeriod} onChange={event => updateBreak(index, 'afterPeriod', event.target.value)} />
                <input className={css.textInput} placeholder="Label" value={item.label} onChange={event => updateBreak(index, 'label', event.target.value)} />
                <input className={css.smallInput} type="number" min={5} max={120} value={item.durationMinutes} onChange={event => updateBreak(index, 'durationMinutes', event.target.value)} />
                <span className={css.ruleLabel}>min</span>
                <button type="button" className={css.removeBtn} onClick={() => removeBreak(index)} aria-label="Remove break" title="Remove break"><DeleteIcon fontSize="small" /></button>
              </div>
            ))}
            <button type="button" className={css.secondaryBtn} onClick={addBreak}>+ Add break</button>
          </div>
        )}

        {step === 2 && (
          <div className={css.field}>
            <label className={css.fieldLabel}>Classes to schedule (default: all)</label>
            <div className={css.checkRow}>{classes.map(cls => <label key={cls._id} className={css.checkOption}><input className="schoolCheckbox" type="checkbox" checked={classIds.includes(cls._id)} onChange={() => toggleClass(cls._id)} />{cls.name}</label>)}</div>
          </div>
        )}

        {step === 3 && (
          <div className={css.field}>
            <label className={css.fieldLabel}>Subjects fixed to a teaching period</label>
            <p className={css.description}>A period is a numbered lesson slot within the day, excluding breaks. For example, period 3 means the third lesson; choose Any day to apply it on every active day.</p>
            {pinnedSubjects.map((item, index) => (
              <div key={index} className={css.ruleRow}>
                <select className={css.select} value={item.subjectId} onChange={event => updatePin(index, 'subjectId', event.target.value)}>{subjects.map(subject => <option key={subject._id} value={subject._id}>{subject.name}</option>)}</select>
                <select className={css.select} value={item.classId} onChange={event => updatePin(index, 'classId', event.target.value)}><option value="">All classes</option>{classes.map(cls => <option key={cls._id} value={cls._id}>{cls.name}</option>)}</select>
                <select className={css.select} value={item.dayOfWeek} onChange={event => updatePin(index, 'dayOfWeek', event.target.value)}><option value="">Any day</option>{ALL_DAYS.map(day => <option key={day} value={day}>{DAY_LABELS[day]}</option>)}</select>
                <span className={css.ruleLabel}>Lesson period #</span>
                <input className={css.smallInput} type="number" min={1} max={12} value={item.periodNumber} onChange={event => updatePin(index, 'periodNumber', event.target.value)} />
                <button type="button" className={css.removeBtn} onClick={() => removePin(index)} aria-label="Remove pinned subject" title="Remove pinned subject"><DeleteIcon fontSize="small" /></button>
              </div>
            ))}
            <button type="button" className={css.secondaryBtn} onClick={addPin}>+ Add pinned subject</button>
          </div>
        )}

        {step === 4 && (
          <div className={css.field}>
            <label className={css.fieldLabel}>Weekly periods per subject (optional — even split by default)</label>
            {subjectFrequency.map((item, index) => (
              <div key={index} className={css.ruleRow}>
                <select className={css.select} value={item.subjectId} onChange={event => updateFrequency(index, 'subjectId', event.target.value)}>{subjects.map(subject => <option key={subject._id} value={subject._id}>{subject.name}</option>)}</select>
                <input className={css.smallInput} type="number" min={0} max={40} value={item.periodsPerWeek} onChange={event => updateFrequency(index, 'periodsPerWeek', event.target.value)} />
                <span>periods/week</span>
                <button type="button" className={css.removeBtn} onClick={() => removeFrequency(index)} aria-label="Remove subject frequency" title="Remove subject frequency"><DeleteIcon fontSize="small" /></button>
              </div>
            ))}
            <button type="button" className={css.secondaryBtn} onClick={addFrequency}>+ Add subject frequency</button>
            <label className={css.checkOption} style={{ marginTop: 14 }}><input className="schoolCheckbox" type="checkbox" checked={aiEnhance} onChange={event => setAiEnhance(event.target.checked)} />Use AI to polish subject order across the day (optional)</label>
          </div>
        )}

        {step === 5 && (
          <div className={css.field}>
            <p>Ready to {hasExistingTimetable ? 'regenerate' : 'generate'} the timetable for {daysActive.length} day(s)/week, {periodsPerDay} periods/day starting {schoolStartTime}.</p>
            <p>{classIds.length ? `${classIds.length} class(es) selected.` : 'All classes included.'} {pinnedSubjects.length} pinned rule(s), {subjectFrequency.length} custom frequency override(s){aiEnhance ? ', AI enhance ON' : ''}.</p>
          </div>
        )}

        <div className={css.modalFooter}>
          <button type="button" className={css.secondaryBtn} onClick={onClose}>Cancel</button>
          {step > 0 && <button type="button" className={css.secondaryBtn} onClick={() => setStep(step - 1)}>Back</button>}
          {step < steps.length - 1 && <button type="button" className={css.primaryBtn} onClick={() => setStep(step + 1)}>Next</button>}
          {step === steps.length - 1 && <button type="button" className={css.primaryBtn} onClick={handleSubmit} disabled={submitting}>{submitting ? 'Working...' : (hasExistingTimetable ? 'Regenerate' : 'Generate')}</button>}
        </div>
      </div>
    </div>
  );
}

// ─── Duplicate from previous term modal ────────────────────────────────────────
function DuplicateModal({ term, session, onClose, onSubmit, submitting, error }) {
  const [fromTerm, setFromTerm] = useState(term === 'third' ? 'second' : term === 'second' ? 'first' : 'third');
  const [fromSession, setFromSession] = useState(session || '');

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={event => event.stopPropagation()}>
        <h3 className={css.modalTitle}>Duplicate From Previous Term</h3>
        {error && <div className={css.modalError}>{error}</div>}
        <div className={css.field}>
          <label className={css.fieldLabel}>Source term</label>
          <select className={css.select} value={fromTerm} onChange={event => setFromTerm(event.target.value)}>
            <option value="first">First</option><option value="second">Second</option><option value="third">Third</option>
          </select>
        </div>
        <div className={css.field}>
          <label className={css.fieldLabel}>Source session</label>
          <input className={css.textInput} value={fromSession} onChange={event => setFromSession(event.target.value)} placeholder="e.g. 2025/2026" />
        </div>
        <p>This copies that timetable's structure and entries into {term} term, {session} as a new draft.</p>
        <div className={css.modalFooter}>
          <button type="button" className={css.secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" className={css.primaryBtn} disabled={submitting || !fromSession} onClick={() => onSubmit({ fromTerm, fromSession, toTerm: term, toSession: session })}>{submitting ? 'Duplicating...' : 'Duplicate'}</button>
        </div>
      </div>
    </div>
  );
}

function DiscardModal({ onClose, onConfirm, submitting, error }) {
  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={event => event.stopPropagation()}>
        <h3 className={css.modalTitle}>Discard timetable draft?</h3>
        <p className={css.modalHint}>This permanently removes the current draft timetable, including its schedule, entries, and manual edits. A new timetable can be generated later.</p>
        {error && <div className={css.modalError}>{error}</div>}
        <div className={css.deleteConfirm} role="alert">
          <strong>This action cannot be undone.</strong>
          <span>Only discard the draft if you are sure you want to start over.</span>
        </div>
        <div className={css.modalFooter}>
          <button type="button" className={css.secondaryBtn} onClick={onClose} disabled={submitting}>Keep draft</button>
          <button type="button" className={css.dangerBtn} onClick={onConfirm} disabled={submitting}><DeleteIcon fontSize="small" /> {submitting ? 'Discarding...' : 'Discard timetable'}</button>
        </div>
      </div>
    </div>
  );
}

function PublishModal({ action, onClose, onConfirm, submitting, error }) {
  const isUnpublish = action === 'unpublish';
  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={event => event.stopPropagation()}>
        <h3 className={css.modalTitle}>{isUnpublish ? 'Unpublish timetable?' : 'Publish timetable?'}</h3>
        <p className={css.modalHint}>{isUnpublish ? 'This will hide the timetable from teachers, students, and parents until it is published again.' : 'This will make the current timetable visible to teachers, students, and parents.'}</p>
        {error && <div className={css.modalError}>{error}</div>}
        <div className={isUnpublish ? css.deleteConfirm : css.publishConfirm} role="alert">
          <strong>{isUnpublish ? 'The timetable will become unavailable to viewers.' : 'Make sure the timetable is ready to share.'}</strong>
          <span>{isUnpublish ? 'You can publish it again after making changes.' : 'Publishing requires all timetable conflicts to be resolved.'}</span>
        </div>
        <div className={css.modalFooter}>
          <button type="button" className={css.secondaryBtn} onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="button" className={isUnpublish ? css.dangerBtn : css.primaryBtn} onClick={onConfirm} disabled={submitting}>{submitting ? (isUnpublish ? 'Unpublishing...' : 'Publishing...') : (isUnpublish ? 'Unpublish timetable' : 'Publish timetable')}</button>
        </div>
      </div>
    </div>
  );
}

export default function TimetableGrid({ mode = 'single', structure, classes = [], entries = [], editable = false, subjects = [], teachers = [], students = [], fetchFn, basePath, term, session, managementEnabled }) {
  const [loading, setLoading] = useState(!structure);
  const [error, setError] = useState('');
  const [timetable, setTimetable] = useState(structure ? { structure, entries } : null);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [viewMode, setViewMode] = useState(() => (typeof window !== 'undefined' && window.localStorage.getItem(`timetable-view-${basePath}`)) || 'day');
  const [editing, setEditing] = useState(null);
  const [selectedMembershipId, setSelectedMembershipId] = useState(students[0]?.membershipId || '');
  const [saving, setSaving] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [publishAction, setPublishAction] = useState('');
  const [wizardError, setWizardError] = useState('');
  const [highlightedCell, setHighlightedCell] = useState('');
  const days = timetable?.structure?.days || [];
  const visibleDays = days.length ? days : [];

  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem(`timetable-view-${basePath}`, viewMode);
  }, [viewMode, basePath]);

  const load = async () => {
    if (!fetchFn || !basePath) return;
    setLoading(true);
    const query = new URLSearchParams({ ...(term ? { term } : {}), ...(session ? { session } : {}), ...(selectedMembershipId ? { membershipId: selectedMembershipId } : {}) });
    const result = await request(fetchFn, `${basePath}/timetable?${query}`);
    if (result.ok && result.data?.timetable) setTimetable(result.data.timetable);
    else if (result.ok && !result.data?.timetable) setTimetable(null);
    else if (!result.ok && result.status !== 404) setError(result.data?.error || 'Unable to load timetable.');
    setLoading(false);
  };

  useEffect(() => { load(); }, [basePath, term, session, selectedMembershipId]); // eslint-disable-line react-hooks/exhaustive-deps

  const runGenerate = async payload => {
    setSaving(true); setWizardError('');
    const result = await request(fetchFn, `${basePath}/timetable/generate`, { method: 'POST', body: JSON.stringify({ term, session, ...payload }) });
    if (result.ok) { setTimetable(result.data.timetable); setShowWizard(false); }
    else setWizardError(result.data?.error || 'Unable to generate timetable.');
    setSaving(false);
  };

  const runDuplicate = async payload => {
    setSaving(true); setWizardError('');
    const result = await request(fetchFn, `${basePath}/timetable/duplicate`, { method: 'POST', body: JSON.stringify(payload) });
    if (result.ok) { setTimetable(result.data.timetable); setShowDuplicate(false); }
    else setWizardError(result.data?.error || 'Unable to duplicate timetable.');
    setSaving(false);
  };

  const runDiscard = async () => {
    setSaving(true); setError('');
    const result = await request(fetchFn, `${basePath}/timetable?term=${encodeURIComponent(term)}&session=${encodeURIComponent(session)}`, { method: 'DELETE' });
    if (result.ok) { setTimetable(null); setShowDiscard(false); }
    else setError(result.data?.error || 'Unable to discard timetable.');
    setSaving(false);
  };

  const publish = async () => {
    setSaving(true); setError('');
    const result = await request(fetchFn, `${basePath}/timetable/publish?term=${encodeURIComponent(term)}&session=${encodeURIComponent(session)}`, { method: 'POST' });
    if (result.ok) { setTimetable(result.data.timetable); setPublishAction(''); }
    else {
      const base = result.data?.error || 'Resolve timetable conflicts before publishing.';
      const conflicts = result.data?.conflicts;
      setError(conflicts?.length ? `${base}: ${conflicts.join(', ')}` : base);
    }
    setSaving(false);
  };

  const unpublish = async () => {
    setSaving(true); setError('');
    const result = await request(fetchFn, `${basePath}/timetable/unpublish?term=${encodeURIComponent(term)}&session=${encodeURIComponent(session)}`, { method: 'POST' });
    if (result.ok) { setTimetable(result.data.timetable); setPublishAction(''); }
    else setError(result.data?.error || 'Unable to unpublish timetable.');
    setSaving(false);
  };

  const markEdited = key => {
    setHighlightedCell(key);
    window.setTimeout(() => setHighlightedCell(''), 2600);
  };

  const saveEntry = async () => {
    if (!editing || editing.type !== 'entry') return;
    setSaving(true); setError('');
    const result = await request(fetchFn, `${basePath}/timetable/entry?term=${encodeURIComponent(term)}&session=${encodeURIComponent(session)}`, { method: 'PATCH', body: JSON.stringify({ dayOfWeek: editing.dayOfWeek, slotOrder: editing.slotOrder, classId: editing.classId, subjectId: editing.subjectId || null, teacherId: editing.teacherId || null }) });
    if (result.ok) { setTimetable(result.data.timetable); setEditing(null); markEdited(`${editing.dayOfWeek}:${editing.slotOrder}:${editing.classId}`); }
    else setError(result.data?.error || 'Unable to save timetable entry.');
    setSaving(false);
  };

  const saveStructure = async () => {
    if (!editing || editing.type === 'entry') return;
    const nextStructure = JSON.parse(JSON.stringify(timetable.structure));
    const nextDayLabels = { ...(timetable.dayLabels || {}) };
    const nextClassLabels = (timetable.classLabels || []).map(item => ({ classId: item.classId, label: item.label }));
    let highlightKey = '';
    if (editing.type === 'slot') {
      const day = nextStructure.days.find(item => item.dayOfWeek === editing.dayOfWeek);
      const slot = day?.slots.find(item => Number(item.order) === Number(editing.slotOrder));
      if (!slot) return;
      Object.assign(slot, { label: editing.label, startTime: editing.startTime, endTime: editing.endTime, kind: editing.kind });
      highlightKey = `${editing.dayOfWeek}:${editing.slotOrder}:shared`;
    } else if (editing.type === 'day') {
      nextDayLabels[editing.dayOfWeek] = editing.label;
      highlightKey = `${editing.dayOfWeek}:day`;
    } else if (editing.type === 'class') {
      const existing = nextClassLabels.find(item => String(item.classId) === String(editing.classId));
      if (existing) existing.label = editing.label;
      else nextClassLabels.push({ classId: editing.classId, label: editing.label });
      highlightKey = `${editing.dayOfWeek}:${editing.classId}:class`;
    }
    setSaving(true); setError('');
    const result = await request(fetchFn, `${basePath}/timetable/structure?term=${encodeURIComponent(term)}&session=${encodeURIComponent(session)}`, { method: 'PATCH', body: JSON.stringify({ structure: nextStructure, dayLabels: nextDayLabels, classLabels: nextClassLabels }) });
    if (result.ok) { setTimetable(result.data.timetable); setEditing(null); markEdited(highlightKey); }
    else setError(result.data?.error || 'Unable to save timetable structure.');
    setSaving(false);
  };

  const deletePeriod = async () => {
    if (!editing || editing.type !== 'slot' || !['period', 'break'].includes(editing.kind)) return;
    setSaving(true); setError('');
    const result = await request(fetchFn, `${basePath}/timetable/period?term=${encodeURIComponent(term)}&session=${encodeURIComponent(session)}`, { method: 'DELETE', body: JSON.stringify({ dayOfWeek: editing.dayOfWeek, slotOrder: editing.slotOrder, periodNumber: editing.periodNumber, kind: editing.kind }) });
    if (result.ok) { setTimetable(result.data.timetable); setEditing(null); markEdited(`${editing.dayOfWeek}:shifted`); }
    else setError(result.data?.error || 'Unable to delete timetable period.');
    setSaving(false);
  };

  const exportPDF = () => {
    if (!timetable) return;
    const snapshot = buildExportSnapshot(timetable, daysToRender, mode, classes, entries);
    const statusLabel = timetable.status === 'published' ? 'Published' : 'Draft';
    const dayBlocks = snapshot.map(day => {
      const headerCells = day.slots.map(slot => `<th>${slot.label}${slot.time ? `<br><small>${slot.time}</small>` : ''}</th>`).join('');
      const bodyRows = day.rows.map(row => {
        const cells = row.cells.map(cell => {
          if (!cell) return '';
          if (cell.shared) return `<td rowspan="${cell.rowSpan}" style="background:#fff8cf;color:#725a0c;text-align:center;font-weight:700">${cell.label}</td>`;
          const tags = [cell.locked ? 'Pinned' : '', cell.needsTeacher ? 'No teacher' : ''].filter(Boolean).join(' · ');
          return `<td style="${cell.isConflict ? 'background:#fdecea' : ''}"><strong>${cell.subjectName || '—'}</strong>${cell.teacherName ? `<br><small>${cell.teacherName}</small>` : ''}${tags ? `<br><small style="color:#b3261e">${tags}</small>` : ''}</td>`;
        }).join('');
        return `<tr><th>${row.className}</th>${cells}</tr>`;
      }).join('');
      return `<h2>${day.dayLabel}</h2><table><thead><tr><th>Class</th>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Timetable</title>
<style>body{font-family:sans-serif;font-size:13px;color:#1a1a2e;padding:24px}h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;color:#1a6e73;margin:20px 0 8px;border-bottom:1px solid #e0f4f5;padding-bottom:4px}table{width:100%;border-collapse:collapse;margin-bottom:12px}th,td{border:1px solid #e0e0e0;padding:6px 8px;text-align:left;font-size:12px;vertical-align:top}th{background:#e0f4f5;font-weight:600}@media print{body{padding:12px}}</style>
</head><body>
<h1>Timetable</h1>
<p style="margin:0 0 12px;color:#607d8b">${term} term · ${session} · ${statusLabel}</p>
${dayBlocks}
</body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const exportExcel = () => {
    if (!timetable) return;
    const snapshot = buildExportSnapshot(timetable, daysToRender, mode, classes, entries);
    const wb = XLSX.utils.book_new();
    snapshot.forEach(day => {
      const header = ['Class', ...day.slots.map(slot => `${slot.label}${slot.time ? ` (${slot.time})` : ''}`)];
      const aoa = [header];
      const merges = [];
      day.rows.forEach((row, rowIndex) => {
        const rowData = [row.className];
        row.cells.forEach((cell, cellIndex) => {
          if (!cell) { rowData.push(''); return; }
          if (cell.shared) {
            rowData.push(cell.label);
            if (cell.rowSpan > 1) merges.push({ s: { r: rowIndex + 1, c: cellIndex + 1 }, e: { r: rowIndex + cell.rowSpan, c: cellIndex + 1 } });
          } else {
            const tags = [cell.locked ? 'Pinned' : '', cell.needsTeacher ? 'No teacher' : '', cell.isConflict ? 'Conflict' : ''].filter(Boolean).join(', ');
            const text = [cell.subjectName, cell.teacherName].filter(Boolean).join(' — ') || '—';
            rowData.push(tags ? `${text} (${tags})` : text);
          }
        });
        aoa.push(rowData);
      });
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      if (merges.length) ws['!merges'] = merges;
      XLSX.utils.book_append_sheet(wb, ws, (day.dayLabel || 'Day').slice(0, 31));
    });
    XLSX.writeFile(wb, `timetable_${term}_${session}`.replace(/[^\w-]+/g, '_') + '.xlsx');
  };

  if (loading) return <div className={css.wrap}><div className={css.card}><TimetableSkeleton /></div></div>;
  if (managementEnabled === false) return <div className={css.wrap}><div className={css.card}><div className={css.premiumLockBanner}>Timetable is available on the Premium school plan.</div></div></div>;
  if (error && !timetable) return <div className={css.wrap}><div className={css.card}><div className={css.state}>{error}</div></div></div>;
  if (!timetable) {
    return (
      <div className={css.wrap}>
        <div className={`${css.card} ${css.empty}`}>
          <div className={css.emptyHeader}>
            <div>
              <h2>Timetable</h2>
              <p className={css.description}>{editable ? 'Generate and publish the school schedule for this term in minutes.' : 'No published timetable is available for this term.'}</p>
            </div>
            {editable && <button className={css.primaryBtn} onClick={() => setShowWizard(true)} disabled={saving}><AddIcon style={{ fontSize: '1rem' }} /> Generate Timetable</button>}
          </div>
        </div>
        {editable && showWizard && <GenerateWizard classes={classes} subjects={subjects} hasExistingTimetable={false} onClose={() => setShowWizard(false)} onSubmit={runGenerate} submitting={saving} error={wizardError} />}
      </div>
    );
  }

  const currentDay = visibleDays.find(day => day.dayOfWeek === selectedDay) || visibleDays[0];
  const daysToRender = (viewMode === 'week' ? visibleDays : [currentDay]).filter(Boolean);

  const renderDayGroup = day => {
    const dayClasses = mode === 'multi' ? classes : [{ _id: entries[0]?.classId || 'student', name: entries[0]?.className || 'My Class' }];
    const classLabel = classId => (timetable.classLabels || []).find(item => String(item.classId) === String(classId))?.label;
    return (
      <React.Fragment key={day.dayOfWeek}>
        <tr className={css.dayHeaderRow}>
          <th className={`${css.dayHeaderCell} ${highlightedCell === `${day.dayOfWeek}:day` ? css.editedCell : ''}`} onClick={() => editable && (setError('') || setEditing({ type: 'day', dayOfWeek: day.dayOfWeek, label: timetable.dayLabels?.[day.dayOfWeek] || DAY_LABELS[day.dayOfWeek] || day.dayOfWeek }))}>{timetable.dayLabels?.[day.dayOfWeek] || DAY_LABELS[day.dayOfWeek] || day.dayOfWeek}</th>
          {day.slots.map(slot => <th key={slot.order} className={`${css.slotHeaderCell} ${highlightedCell === `${day.dayOfWeek}:${slot.order}:shared` ? css.editedCell : ''}`} onClick={() => editable && (setError('') || setEditing({ type: 'slot', dayOfWeek: day.dayOfWeek, slotOrder: slot.order, periodNumber: slot.periodNumber, kind: slot.kind, label: slot.label, startTime: slot.startTime || '', endTime: slot.endTime || '' }))}>{slot.label}{slot.startTime && <small>{slot.startTime}–{slot.endTime}</small>}</th>)}
        </tr>
        {dayClasses.map((schoolClass, index) => (
          <tr key={`${day.dayOfWeek}-${schoolClass._id}`}>
            <th className={`${css.className} ${highlightedCell === `${day.dayOfWeek}:${schoolClass._id}:class` ? css.editedCell : ''}`} onClick={() => editable && (setError('') || setEditing({ type: 'class', dayOfWeek: day.dayOfWeek, classId: schoolClass._id, label: classLabel(schoolClass._id) || schoolClass.name }))}>{classLabel(schoolClass._id) || schoolClass.name}</th>
            {day.slots.map(slot => {
              if (slot.kind !== 'period') {
                if (index !== 0) return null; // merged into the shared cell rendered on the first class row
                return <td key={slot.order} rowSpan={dayClasses.length} className={`${css.sharedCell} ${css[`shared${slot.kind}`] || ''} ${highlightedCell === `${day.dayOfWeek}:${slot.order}:shared` || (highlightedCell === `${day.dayOfWeek}:shifted` && slot.kind === 'period') ? css.editedCell : ''}`} onClick={() => editable && (setError('') || setEditing({ type: 'slot', dayOfWeek: day.dayOfWeek, slotOrder: slot.order, periodNumber: slot.periodNumber, kind: slot.kind, label: slot.label, startTime: slot.startTime || '', endTime: slot.endTime || '' }))}>{slot.label}</td>;
              }
              const entry = entryFor(timetable.entries, day.dayOfWeek, slot.order, mode === 'multi' ? schoolClass._id : null);
              return (
                <Cell
                  key={slot.order}
                  slot={slot}
                  entry={entry}
                  editable={editable}
                  highlighted={highlightedCell === `${day.dayOfWeek}:${slot.order}:${schoolClass._id}` || highlightedCell === `${day.dayOfWeek}:shifted`}
                  onEdit={({ entry: clickedEntry, slot: selectedSlot }) => { setError(''); setEditing({ type: 'entry', dayOfWeek: day.dayOfWeek, slotOrder: selectedSlot.order, classId: schoolClass._id, subjectId: clickedEntry?.subjectId || '', teacherId: clickedEntry?.teacherId || '' }); }}
                />
              );
            })}
          </tr>
        ))}
      </React.Fragment>
    );
  };

  const eligibleTeachers = editing?.subjectId
    ? teachers.filter(teacher => (teacher.assignedClasses || []).some(classId => String(classId) === String(editing.classId)) && (teacher.assignedSubjects || []).some(subjectId => String(subjectId) === String(editing.subjectId)))
    : [];

  return (
    <div className={css.wrap}>
      <div className={css.card}>
        <div className={css.header}>
          <div><p className={css.kicker}>Academic schedule</p><h2>Timetable</h2><span>{timetable.status === 'published' ? 'Published' : 'Draft'} · {term} term · {session}</span></div>
          {editable && (
            <div className={css.actions}>
              <button className={css.secondaryBtn} onClick={() => setShowDuplicate(true)} disabled={saving}>Duplicate from term</button>
              <button className={css.secondaryBtn} onClick={() => setShowWizard(true)} disabled={saving}>Regenerate</button>
              {timetable.status !== 'published' && <button className={css.dangerBtn} onClick={() => { setError(''); setShowDiscard(true); }} disabled={saving}>Discard</button>}
              {timetable.status !== 'published' && <button className={css.primaryBtn} onClick={() => { setError(''); setPublishAction('publish'); }} disabled={saving}>Publish</button>}
              {timetable.status === 'published' && <button className={css.secondaryBtn} onClick={() => { setError(''); setPublishAction('unpublish'); }} disabled={saving}>Unpublish</button>}
            </div>
          )}
        </div>
        {error && <div className={css.warning}>{error}</div>}
        {timetable.generationMeta?.aiNote && <div className={css.info}>{timetable.generationMeta.aiNote}</div>}
        {students.length > 0 && <label className={css.childPicker}>Child<select value={selectedMembershipId} onChange={event => setSelectedMembershipId(event.target.value)}>{students.map(child => <option key={child.membershipId} value={child.membershipId}>{child.firstName} {child.lastName}</option>)}</select></label>}
      </div>

      <div className={css.card}>
        <div className={css.viewToggleRow}>
          <div className={css.viewToggle}>
            <button className={viewMode === 'day' ? css.viewActive : ''} onClick={() => setViewMode('day')}>Day view</button>
            <button className={viewMode === 'week' ? css.viewActive : ''} onClick={() => setViewMode('week')}>Week view</button>
          </div>
          <div className={css.drawerExportRow}>
            <button type="button" className={css.drawerExportBtn} onClick={exportPDF}>🖨 Export PDF</button>
            <button type="button" className={css.drawerExportBtn} style={{ background: 'linear-gradient(135deg,#1a6e73,#2a9d8f)' }} onClick={exportExcel}>📊 Export Excel</button>
          </div>
        </div>
        {viewMode === 'day' && <div className={css.dayTabs}>{visibleDays.map(day => <button key={day.dayOfWeek} className={selectedDay === day.dayOfWeek ? css.dayActive : ''} onClick={() => setSelectedDay(day.dayOfWeek)}>{DAY_LABELS[day.dayOfWeek]}</button>)}</div>}
        <div className={css.tableWrap}><table className={css.table}><tbody>{daysToRender.map(renderDayGroup)}</tbody></table></div>
      </div>

      {editing && <EditModal editing={editing} subjects={subjects} eligibleTeachers={eligibleTeachers} onClose={() => { setEditing(null); setError(''); }} onChange={setEditing} onSave={editing.type === 'entry' ? saveEntry : saveStructure} onDelete={deletePeriod} saving={saving} error={error} />}
      {editable && showWizard && <GenerateWizard classes={classes} subjects={subjects} hasExistingTimetable onClose={() => setShowWizard(false)} onSubmit={runGenerate} submitting={saving} error={wizardError} />}
      {editable && showDuplicate && <DuplicateModal term={term} session={session} onClose={() => setShowDuplicate(false)} onSubmit={runDuplicate} submitting={saving} error={wizardError} />}
      {editable && showDiscard && <DiscardModal onClose={() => { setShowDiscard(false); setError(''); }} onConfirm={runDiscard} submitting={saving} error={error} />}
      {editable && publishAction && <PublishModal action={publishAction} onClose={() => { setPublishAction(''); setError(''); }} onConfirm={publishAction === 'publish' ? publish : unpublish} submitting={saving} error={error} />}
    </div>
  );
}
